import { useState, useEffect, useCallback } from 'react';
import { playBell } from '@/lib/bellSound';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, ShieldCheck, AlertTriangle, Brain, BarChart3, Save, History, Trash2, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Auth } from '@/components/Auth';
import { HedgeExcelImport } from '@/components/HedgeExcelImport';
import { HedgeResultsPanel } from '@/components/HedgeResultsPanel';
import { HedgeStrategyGuide } from '@/components/HedgeStrategyGuide';
import { MultiLLMHedgeResults } from '@/components/MultiLLMHedgeResults';
import { PortfolioHedgeSimulator } from '@/components/PortfolioHedgeSimulator';
import { HedgePosition, HedgeAnalysis, MarginData, analyzeHedge } from '@/lib/hedgeAnalysis';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';

interface ModelResponse {
  id: string;
  label: string;
  status: 'pending' | 'fetching' | 'success' | 'error';
  error?: string | null;
  result?: any;
}

interface SavedSession {
  id: string;
  created_at: string;
  positions_count: number;
  total_portfolio_value: number;
  margin_used: number;
  positions_data: HedgePosition[];
  analyses_data: HedgeAnalysis[];
  llm_results: ModelResponse[];
}

const INITIAL_MODELS: ModelResponse[] = [
  { id: 'google/gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite', status: 'pending' },
  { id: 'google/gemini-2.5-flash', label: 'Gemini 2.5 Flash', status: 'pending' },
];

const OPTIONAL_MODELS: ModelResponse[] = [
  { id: 'openai/gpt-5-mini', label: 'GPT-5 Mini', status: 'pending' },
  { id: 'openai/gpt-5', label: 'GPT-5', status: 'pending' },
];

const HedgeProtection = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [positions, setPositions] = useState<HedgePosition[]>([]);
  const [analyses, setAnalyses] = useState<HedgeAnalysis[]>([]);
  const [marginUsed, setMarginUsed] = useState<number>(0);
  const [llmModels, setLlmModels] = useState<ModelResponse[]>(INITIAL_MODELS);
  const [llmLoading, setLlmLoading] = useState(false);
  const [savedSessions, setSavedSessions] = useState<SavedSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [saving, setSaving] = useState(false);
  const [marginData, setMarginData] = useState<MarginData | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Load saved sessions when user is available
  const loadSavedSessions = useCallback(async () => {
    if (!user) return;
    setLoadingSessions(true);
    try {
      const { data, error } = await supabase
        .from('hedge_analyses')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      setSavedSessions((data as any[]) || []);
    } catch (e: any) {
      console.error('Failed to load saved sessions:', e);
    } finally {
      setLoadingSessions(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) loadSavedSessions();
  }, [user, loadSavedSessions]);

  const autoSaveSession = async (
    posData: HedgePosition[],
    anlData: HedgeAnalysis[],
    models: ModelResponse[],
    margin: number,
    silent = false,
  ) => {
    if (!user || !posData.length) return;
    setSaving(true);
    try {
      const successModels = models.filter(m => m.status === 'success');
      if (successModels.length === 0) return;
      const totalValue = posData.reduce((s, p) => s + p.totalValue, 0);
      const { error } = await supabase.from('hedge_analyses').insert({
        user_id: user.id,
        positions_data: posData as any,
        analyses_data: anlData as any,
        llm_results: successModels.map(m => ({ id: m.id, label: m.label, status: m.status, result: m.result })) as any,
        margin_used: margin,
        total_portfolio_value: totalValue,
        positions_count: posData.length,
      });
      if (error) throw error;
      if (!silent) toast({ title: 'Auto-Saved', description: 'Analysis saved automatically.' });
      loadSavedSessions();
    } catch (e: any) {
      console.error('Auto-save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  const saveCurrentSession = async () => {
    await autoSaveSession(positions, analyses, llmModels, marginUsed);
  };

  const restoreSession = (session: SavedSession) => {
    const restoredPositions = session.positions_data as HedgePosition[];
    setPositions(restoredPositions);
    setAnalyses(session.analyses_data as HedgeAnalysis[]);
    setMarginUsed(session.margin_used);

    // Restore LLM results with correct status
    const restoredModels: ModelResponse[] = INITIAL_MODELS.map(im => {
      const saved = (session.llm_results as any[])?.find((r: any) => r.id === im.id);
      return saved ? { ...im, status: 'success' as const, result: saved.result } : im;
    });
    // Also restore optional models if they were saved
    OPTIONAL_MODELS.forEach(om => {
      const saved = (session.llm_results as any[])?.find((r: any) => r.id === om.id);
      if (saved) {
        restoredModels.push({ ...om, status: 'success' as const, result: saved.result });
      }
    });
    setLlmModels(restoredModels);
    setLlmLoading(false);

    toast({ title: 'Session Restored', description: `Loaded analysis from ${format(new Date(session.created_at), 'dd MMM yyyy HH:mm')}` });
  };

  const deleteSession = async (sessionId: string) => {
    try {
      const { error } = await supabase.from('hedge_analyses').delete().eq('id', sessionId);
      if (error) throw error;
      setSavedSessions(prev => prev.filter(s => s.id !== sessionId));
      toast({ title: 'Session Deleted' });
    } catch (e: any) {
      toast({ title: 'Delete Failed', description: e.message, variant: 'destructive' });
    }
  };

  const runLLMAnalysis = async (importedPositions: HedgePosition[], margin: number, computedAnalyses: HedgeAnalysis[]) => {
    setLlmLoading(true);
    setLlmModels(INITIAL_MODELS.map(m => ({ ...m, status: 'fetching' })));

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/hedge-analysis`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    };

    const promises = INITIAL_MODELS.map(async (m) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000);
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({ positions: importedPositions, marginUsed: margin, model: m.id }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          const errBody = await response.text();
          return { ...m, status: 'error' as const, error: `HTTP ${response.status}: ${errBody}`, result: null };
        }

        const data = await response.json();
        return {
          id: m.id,
          label: m.label,
          status: data.error ? 'error' as const : 'success' as const,
          error: data.error || null,
          result: data.result,
        };
      } catch (e: any) {
        return { ...m, status: 'error' as const, error: e.message || 'Failed', result: null };
      }
    });

    const results = await Promise.all(promises);
    setLlmModels(results);

    const successCount = results.filter(r => r.status === 'success').length;
    if (successCount > 0) {
      toast({ title: 'AI Analysis Complete', description: `${successCount}/${INITIAL_MODELS.length} models returned results.` });
      playBell();
      // Auto-save after initial analysis
      autoSaveSession(importedPositions, computedAnalyses, results, margin, true);
    } else {
      toast({ title: 'AI Analysis Failed', description: 'No models returned results.', variant: 'destructive' });
    }
    setLlmLoading(false);
  };

  const runOptionalModel = async (modelId: string) => {
    if (!positions.length) return;
    const m = OPTIONAL_MODELS.find(om => om.id === modelId);
    if (!m) return;
    setLlmModels(prev => [...prev.filter(p => p.id !== m.id), { ...m, status: 'fetching' }]);

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/hedge-analysis`;
    const hdrs = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000);
      const response = await fetch(url, {
        method: 'POST',
        headers: hdrs,
        body: JSON.stringify({ positions, marginUsed, model: m.id }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      let res: ModelResponse;
      if (!response.ok) {
        const errBody = await response.text();
        res = { ...m, status: 'error', error: `HTTP ${response.status}: ${errBody}` };
      } else {
        const data = await response.json();
        res = { ...m, status: data.error ? 'error' : 'success', error: data.error || null, result: data.result };
      }
      setLlmModels(prev => {
        const updated = prev.map(p => p.id === m.id ? res : p);
        // Auto-save with updated models
        if (res.status === 'success') {
          autoSaveSession(positions, analyses, updated, marginUsed, true);
        }
        return updated;
      });
      toast({
        title: res.status === 'success' ? `${m.label} Complete` : `${m.label} Failed`,
        description: res.status === 'success' ? 'Additional model returned results.' : (res.error || 'Request failed.'),
        variant: res.status === 'success' ? 'default' : 'destructive',
      });
    } catch (e: any) {
      setLlmModels(prev => prev.map(p => p.id === m.id ? { ...m, status: 'error', error: e.message } : p));
    }
  };

  const handleImportComplete = (imported: HedgePosition[], margin: number, mData?: MarginData) => {
    setPositions(imported);
    setMarginUsed(margin);
    if (mData) setMarginData(mData);
    const totalValue = imported.reduce((s, p) => s + p.totalValue, 0);
    const results = imported.map(p => analyzeHedge(p, totalValue));
    setAnalyses(results);
    runLLMAnalysis(imported, margin, results);
  };

  if (!user) {
    return <Auth />;
  }

  const totalPortfolioValue = positions.reduce((s, p) => s + p.totalValue, 0);
  const hasSuccessfulModels = llmModels.some(m => m.status === 'success');

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-500/10 rounded-xl">
              <ShieldCheck className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Hedge Protection</h1>
              <p className="text-muted-foreground">Options hedging strategies analysed by up to 4 AI models</p>
            </div>
          </div>
        </div>

        {positions.length === 0 ? (
          <div className="space-y-8">
            {/* Saved Sessions Panel */}
            {!loadingSessions && savedSessions.length > 0 && (
              <Card className="border-primary/30 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="h-5 w-5 text-primary" />
                    Previous Analyses
                  </CardTitle>
                  <CardDescription>
                    Reload a previous hedge analysis instead of running the full research again.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {savedSessions.map(session => (
                      <div
                        key={session.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">
                              {format(new Date(session.created_at), 'dd MMM yyyy — HH:mm')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {session.positions_count} positions · {Number(session.total_portfolio_value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })} · Margin {session.margin_used}% · {(session.llm_results as any[])?.length || 0} AI models
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="sm" variant="default" onClick={() => restoreSession(session)} className="gap-1">
                            <History className="h-3 w-3" />
                            Reload
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteSession(session.id)} className="text-muted-foreground hover:text-destructive">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Strategy Guide */}
            <HedgeStrategyGuide />

            {/* Import Section */}
            <div className="max-w-xl mx-auto">
              <Card className="shadow-lg border-primary/20">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Import Portfolio to Start Analysis
                  </CardTitle>
                  <CardDescription>
                    Upload your portfolio Excel file and specify your current margin usage.
                    Up to 4 AI models will analyse your positions and recommend hedge strategies.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <HedgeExcelImport onImportComplete={handleImportComplete} />
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-muted-foreground">
                {positions.length} positions | Value: {totalPortfolioValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} | Margin: {marginUsed}%
              </p>
              <div className="flex items-center gap-2">
                {hasSuccessfulModels && (
                  <Button variant="outline" size="sm" onClick={saveCurrentSession} disabled={saving} className="gap-1">
                    <Save className="h-3 w-3" />
                    {saving ? 'Saving...' : 'Save Analysis'}
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => {
                  setPositions([]);
                  setAnalyses([]);
                  setLlmModels(INITIAL_MODELS);
                }}>
                  Clear & Re-import
                </Button>
              </div>
            </div>

            <Tabs defaultValue="simulator" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="simulator" className="gap-1">
                  <BarChart3 className="h-3 w-3" />
                  Hedge Simulator
                </TabsTrigger>
                <TabsTrigger value="ai-analysis" className="gap-1">
                  <Brain className="h-3 w-3" />
                  AI Analysis
                </TabsTrigger>
                <TabsTrigger value="calculations">
                  Calculations
                </TabsTrigger>
                <TabsTrigger value="guide">
                  Strategy Guide
                </TabsTrigger>
              </TabsList>

              <TabsContent value="simulator" className="mt-4">
                <PortfolioHedgeSimulator analyses={analyses} positions={positions} totalPortfolioValue={totalPortfolioValue} />
              </TabsContent>

              <TabsContent value="ai-analysis" className="mt-4">
                <MultiLLMHedgeResults models={llmModels} loading={llmLoading} onTriggerOptionalModel={runOptionalModel} optionalModels={OPTIONAL_MODELS.map(m => ({ id: m.id, label: m.label }))} analyses={analyses} marginData={marginData} positions={positions} />
              </TabsContent>

              <TabsContent value="calculations" className="mt-4">
                <HedgeResultsPanel analyses={analyses} totalPortfolioValue={totalPortfolioValue} />
              </TabsContent>

              <TabsContent value="guide" className="mt-4">
                <HedgeStrategyGuide />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
};

export default HedgeProtection;
